import React, { useEffect, useState, createElement } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { CheckCircle2, XCircle, Wallet, TrendingDown, ShieldCheck, Clock, BookOpen, Calendar, Tag, Calculator, Smartphone, Download, Target, ShoppingCart, Lock, Star, ArrowRight, X } from 'lucide-react';
const FadeIn = ({
  children,
  delay = 0,
  className = ''
}: {
  children: React.ReactNode;
  delay?: number;
  className?: string;
}) => <motion.div initial={{
  opacity: 0,
  y: 30
}} whileInView={{
  opacity: 1,
  y: 0
}} viewport={{
  once: true,
  margin: '-50px'
}} transition={{
  duration: 0.6,
  delay,
  ease: 'easeOut'
}} className={className} data-id="element-0">
    {children}
  </motion.div>;
const CTAButton = ({
  className = ''
}: {
  className?: string;
}) => <button onClick={() => document.getElementById('pricing')?.scrollIntoView({
  behavior: 'smooth'
})} className={`w-full sm:w-auto bg-forest hover:bg-forest-light text-white font-bold py-4 px-8 rounded-full shadow-lg transform transition-transform hover:scale-105 active:scale-95 flex items-center justify-center gap-2 text-lg ${className}`} data-id="element-1">
    ¡QUIERO SER LA CFO DE MI HOGAR!
    <ArrowRight className="w-5 h-5" data-id="element-2" />
  </button>;
declare global {
  interface Window {
    fbq: (...args: any[]) => void;
  }
}
export function App() {
  const [modalOpen, setModalOpen] = useState<'privacy' | 'terms' | null>(null);
  useEffect(() => {
    // Meta Pixel initialization
    if (!window.fbq) {
      const n: any = window.fbq = function (...args: any[]) {
        n.callMethod ? n.callMethod.apply(n, args) : n.queue.push(args);
      };
      n.push = n;
      n.loaded = true;
      n.version = '2.0';
      n.queue = [] as any[];
      const t = document.createElement('script');
      t.async = true;
      t.src = 'https://connect.facebook.net/en_US/fbevents.js';
      document.head.appendChild(t);
    }
    window.fbq('init', '1007229342482935');
    window.fbq('track', 'PageView');
  }, []);
  const handleAddToCart = () => {
    window.fbq('track', 'AddToCart');
    window.open('https://vidaconclarity.com/cart/42669569605719:1', '_blank');
  };
  return <div className="min-h-screen bg-cream font-sans text-gray-800 overflow-x-hidden" data-id="element-3">
      {/* 1. HERO SECTION */}
      <section className="relative pt-12 pb-20 lg:pt-20 lg:pb-28 px-6 max-w-7xl mx-auto" data-id="element-4">
        <div className="flex flex-col lg:flex-row items-center gap-12 lg:gap-20" data-id="element-5">
          <FadeIn className="flex-1 text-center lg:text-left z-10" data-id="element-6">
            <div className="inline-block bg-gold/20 text-gold-dark font-bold px-4 py-1.5 rounded-full text-sm mb-6 uppercase tracking-wide" data-id="element-7">
              El Sistema #1 para Latinas en USA
            </div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-black text-forest leading-tight mb-6" data-id="element-8">
              CFO DEL HOGAR: <br data-id="element-9" />
              <span className="text-gold-dark" data-id="element-10">El Sistema Binder Digital</span>
            </h1>
            <p className="text-lg md:text-xl text-gray-700 mb-8 leading-relaxed font-medium" data-id="element-11">
              Toma el control total de tus dólares en USA sin estrés, sin cuotas
              y sin que el dinero se te escape de las manos.
            </p>
            <div className="flex flex-col items-center lg:items-start" data-id="element-12">
              <CTAButton data-id="element-13" />
            </div>
          </FadeIn>

          <FadeIn delay={0.2} className="flex-1 w-full max-w-md lg:max-w-full relative" data-id="element-14">
            <div className="absolute inset-0 bg-gold/20 rounded-[3rem] transform rotate-3 scale-105 -z-10" data-id="element-15"></div>
            <img src="https://cdn.magicpatterns.com/uploads/gWARH7mbLUFU2ae12bRDvk/CDF_MUJER_.png" alt="Mujer latina sonriendo mostrando el Binder Digital en su celular" className="w-full h-auto rounded-[3rem] shadow-2xl object-cover border-4 border-white" data-id="element-16" />
          </FadeIn>
        </div>
      </section>

      {/* 2. PAIN POINTS */}
      <section className="py-20 bg-white px-6" data-id="element-17">
        <div className="max-w-3xl mx-auto" data-id="element-18">
          <FadeIn data-id="element-19">
            <h2 className="text-3xl md:text-4xl font-bold text-forest mb-8 text-center leading-tight" data-id="element-20">
              ¿Alguna vez has sentido ese nudo en el estómago al abrir la app de
              tu banco y ver que tu saldo es mucho menor de lo que pensabas?
            </h2>
          </FadeIn>
          <FadeIn delay={0.1} data-id="element-21">
            <p className="text-lg text-gray-700 mb-6 leading-relaxed" data-id="element-22">
              <span className="font-bold text-forest" data-id="element-23">No estás sola.</span> En
              Estados Unidos, el dinero parece "evaporarse" entre la renta, el
              súper, la gasolina y esos pequeños gastos que no anotamos.
            </p>
            <div className="bg-cream-dark p-6 rounded-2xl border-l-4 border-gold mb-6" data-id="element-24">
              <p className="text-xl font-bold text-gray-800 italic" data-id="element-25">
                "El problema no es cuánto ganas, sino que tu dinero no tiene un
                nombre."
              </p>
            </div>
            <p className="text-lg text-gray-700 leading-relaxed" data-id="element-26">
              Vives en modo "supervivencia", esperando el próximo cheque para
              cubrir el hueco que dejó el anterior. Has intentado usar apps
              complicadas o libretas que olvidas llenar, pero nada se adapta a
              tu realidad como mujer latina que maneja un hogar.
            </p>
          </FadeIn>
        </div>
      </section>

      {/* 3. WHY OTHER SOLUTIONS FAILED */}
      <section className="py-20 bg-cream px-6" data-id="element-27">
        <div className="max-w-4xl mx-auto" data-id="element-28">
          <FadeIn data-id="element-29">
            <h2 className="text-3xl font-bold text-center text-forest mb-12" data-id="element-30">
              ¿Por qué lo que has intentado no ha funcionado?
            </h2>
          </FadeIn>
          <div className="grid md:grid-cols-2 gap-6" data-id="element-31">
            {['Las apps americanas no entienden cómo dividimos el dinero para la familia o las remesas.', 'El presupuesto tradicional es rígido y no te permite ser flexible cuando surge una emergencia.', 'Intentaste usar cupones, pero sin un sistema terminaste gastando más en cosas que no necesitabas.', 'No tienes una separación visual clara entre lo que es para la renta y lo que es para el café del viernes.'].map((text, i) => <FadeIn key={i} delay={i * 0.1} className="bg-white p-6 rounded-2xl shadow-sm flex gap-4 items-start border border-gray-100" data-id="element-32">
                <XCircle className="w-6 h-6 text-red-500 flex-shrink-0 mt-1" data-id="element-33" />
                <p className="text-gray-700 font-medium" data-id="element-34">{text}</p>
              </FadeIn>)}
          </div>
        </div>
      </section>

      {/* 4. THE SOLUTION & 5. BENEFITS */}
      <section className="py-20 bg-forest text-white px-6" data-id="element-35">
        <div className="max-w-5xl mx-auto" data-id="element-36">
          <FadeIn className="text-center mb-16" data-id="element-37">
            <div className="inline-block bg-gold text-forest font-bold px-4 py-1.5 rounded-full text-sm mb-6 uppercase tracking-wide" data-id="element-38">
              La Solución
            </div>
            <h2 className="text-3xl md:text-5xl font-black mb-6" data-id="element-39">
              Presentamos: El Método "Cash Stuffing" Digital
            </h2>
            <p className="text-xl text-cream/90 max-w-3xl mx-auto leading-relaxed" data-id="element-40">
              He transformado el famoso sistema de "relleno de sobres" en una
              herramienta 100% digital y adaptada a la vida en USA. Es un
              sistema visual donde{' '}
              <span className="text-gold font-bold" data-id="element-41">
                cada dólar tiene una misión
              </span>{' '}
              antes de que lo gastes.
            </p>
          </FadeIn>

          <div className="grid md:grid-cols-2 gap-8 mt-12" data-id="element-42">
            <FadeIn delay={0.1} className="bg-forest-dark p-8 rounded-3xl border border-forest-light/30" data-id="element-43">
              <Wallet className="w-10 h-10 text-gold mb-4" data-id="element-44" />
              <h3 className="text-2xl font-bold mb-3" data-id="element-45">Claridad Total</h3>
              <p className="text-cream/80" data-id="element-46">
                Sabrás exactamente cuánto puedes gastar en comida o gustos sin
                poner en riesgo la renta.
              </p>
            </FadeIn>
            <FadeIn delay={0.2} className="bg-forest-dark p-8 rounded-3xl border border-forest-light/30" data-id="element-47">
              <TrendingDown className="w-10 h-10 text-gold mb-4" data-id="element-48" />
              <h3 className="text-2xl font-bold mb-3" data-id="element-49">Ahorro Real</h3>
              <p className="text-cream/80" data-id="element-50">
                Aprenderás la "Ruta del Cupón Ganador" para bajar tu cuenta del
                súper hasta un 50% usando solo tu celular.
              </p>
            </FadeIn>
            <FadeIn delay={0.3} className="bg-forest-dark p-8 rounded-3xl border border-forest-light/30" data-id="element-51">
              <ShieldCheck className="w-10 h-10 text-gold mb-4" data-id="element-52" />
              <h3 className="text-2xl font-bold mb-3" data-id="element-53">Paz Mental</h3>
              <p className="text-cream/80" data-id="element-54">
                Construirás tu primer fondo de emergencia de $500 para dejar de
                tenerle miedo a los imprevistos del carro o la casa.
              </p>
            </FadeIn>
            <FadeIn delay={0.4} className="bg-forest-dark p-8 rounded-3xl border border-forest-light/30" data-id="element-55">
              <Clock className="w-10 h-10 text-gold mb-4" data-id="element-56" />
              <h3 className="text-2xl font-bold mb-3" data-id="element-57">Control Diario</h3>
              <p className="text-cream/80" data-id="element-58">
                Un sistema de rastreo de 30 segundos que detiene las "fugas de
                dinero" antes de que vacíen tu cuenta.
              </p>
            </FadeIn>
          </div>
        </div>
      </section>

      {/* 6. WHAT's INCLUDED */}
      <section className="py-20 bg-white px-6" data-id="element-59">
        <div className="max-w-4xl mx-auto" data-id="element-60">
          <FadeIn className="text-center mb-12" data-id="element-61">
            <h2 className="text-3xl md:text-4xl font-bold text-forest mb-4" data-id="element-62">
              Contenido del Pack "CFO del Hogar"
            </h2>
            <p className="text-lg text-gray-600" data-id="element-63">
              Todo lo que necesitas para tomar el control hoy mismo.
            </p>
          </FadeIn>

          <div className="space-y-4" data-id="element-64">
            {[{
            icon: BookOpen,
            text: 'La Guía Maestra: El Método de los 4 Bolsillos Digitales.'
          }, {
            icon: Calendar,
            text: "Plantilla de Calendario de Pagos (Bills) para no pagar ni un 'late fee' más."
          }, {
            icon: Tag,
            text: 'El Rastreador de Cupones y Cashback (Ibotta/Fetch Rewards) paso a paso.'
          }, {
            icon: Calculator,
            text: 'Calculadora Quincenal de Distribución de Ingresos.'
          }].map((item, i) => <FadeIn key={i} delay={i * 0.1} className="flex items-center gap-4 p-5 bg-cream rounded-2xl border border-cream-dark" data-id="element-65">
                <div className="bg-white p-3 rounded-full shadow-sm" data-id="element-66">
                  <item.icon className="w-6 h-6 text-forest" data-id="element-67" />
                </div>
                <p className="text-lg font-semibold text-gray-800" data-id="element-68">
                  {item.text}
                </p>
              </FadeIn>)}

            <FadeIn delay={0.5} className="mt-8 bg-gradient-to-r from-gold/20 to-gold/5 border-2 border-gold p-6 rounded-2xl flex items-start gap-4" data-id="element-69">
              <div className="bg-gold p-3 rounded-full shadow-sm flex-shrink-0" data-id="element-70">
                <Star className="w-6 h-6 text-white" data-id="element-71" />
              </div>
              <div data-id="element-72">
                <h4 className="text-xl font-bold text-gold-dark mb-1" data-id="element-73">
                  BONO EXCLUSIVO
                </h4>
                <p className="text-lg font-semibold text-gray-800" data-id="element-74">
                  Lista Maestra de Apps de Ahorro que realmente funcionan en
                  USA.
                </p>
              </div>
            </FadeIn>
          </div>
        </div>
      </section>

      {/* 7. WHO IT'S FOR / NOT FOR */}
      <section className="py-20 bg-cream px-6" data-id="element-75">
        <div className="max-w-5xl mx-auto grid md:grid-cols-2 gap-8" data-id="element-76">
          <FadeIn className="bg-white p-8 rounded-3xl shadow-sm border-t-4 border-forest" data-id="element-77">
            <h3 className="text-2xl font-bold text-forest mb-6 flex items-center gap-2" data-id="element-78">
              <CheckCircle2 className="w-6 h-6" data-id="element-79" /> Este sistema ES para ti si:
            </h3>
            <ul className="space-y-4" data-id="element-80">
              {['Vives en USA y sientes que el costo de vida te está asfixiando.', 'Quieres ahorrar para una meta (casa, viaje, papeles) pero no ves por dónde empezar.', 'Eres la encargada de las compras y finanzas de tu familia.'].map((text, i) => <li key={i} className="flex items-start gap-3" data-id="element-81">
                  <CheckCircle2 className="w-5 h-5 text-forest flex-shrink-0 mt-0.5" data-id="element-82" />
                  <span className="text-gray-700" data-id="element-83">{text}</span>
                </li>)}
            </ul>
          </FadeIn>

          <FadeIn delay={0.2} className="bg-white p-8 rounded-3xl shadow-sm border-t-4 border-red-500" data-id="element-84">
            <h3 className="text-2xl font-bold text-red-600 mb-6 flex items-center gap-2" data-id="element-85">
              <XCircle className="w-6 h-6" data-id="element-86" /> Este sistema NO es para ti si:
            </h3>
            <ul className="space-y-4" data-id="element-87">
              {['Buscas una inversión mágica para hacerte rica de la noche a la mañana.', 'No estás dispuesta a dedicar 10 minutos a la semana a revisar tus números.'].map((text, i) => <li key={i} className="flex items-start gap-3" data-id="element-88">
                  <XCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" data-id="element-89" />
                  <span className="text-gray-700" data-id="element-90">{text}</span>
                </li>)}
            </ul>
          </FadeIn>
        </div>
      </section>

      {/* 8. HOW IT WORKS */}
      <section className="py-20 bg-white px-6" data-id="element-91">
        <div className="max-w-5xl mx-auto" data-id="element-92">
          <FadeIn className="text-center mb-16" data-id="element-93">
            <h2 className="text-3xl md:text-4xl font-bold text-forest mb-4" data-id="element-94">
              Cómo funciona
            </h2>
            <p className="text-lg text-gray-600" data-id="element-95">
              Tres simples pasos para tomar el control.
            </p>
          </FadeIn>

          <div className="grid md:grid-cols-3 gap-8 relative" data-id="element-96">
            <div className="hidden md:block absolute top-12 left-[15%] right-[15%] h-0.5 bg-cream-dark -z-10" data-id="element-97"></div>

            {[{
            icon: Download,
            title: '1. Descargas',
            desc: 'Recibes el acceso inmediato a tu Binder y Guía en PDF.'
          }, {
            icon: Target,
            title: '2. Asignas',
            desc: "Sigues la fórmula para dividir tu próximo cheque en tus nuevos 'sobres digitales'."
          }, {
            icon: ShoppingCart,
            title: '3. Ahorras',
            desc: 'Aplicas la estrategia de cupones digitales en tu próxima visita al súper.'
          }].map((step, i) => <FadeIn key={i} delay={i * 0.2} className="flex flex-col items-center text-center" data-id="element-98">
                <div className="w-24 h-24 bg-cream rounded-full flex items-center justify-center mb-6 border-4 border-white shadow-lg" data-id="element-99">
                  <step.icon className="w-10 h-10 text-forest" data-id="element-100" />
                </div>
                <h3 className="text-2xl font-bold text-gray-800 mb-3" data-id="element-101">
                  {step.title}
                </h3>
                <p className="text-gray-600" data-id="element-102">{step.desc}</p>
              </FadeIn>)}
          </div>
        </div>
      </section>

      {/* 9. GUARANTEE */}
      <section className="py-16 bg-cream-dark px-6" data-id="element-103">
        <div className="max-w-3xl mx-auto text-center" data-id="element-104">
          <FadeIn className="bg-white p-8 md:p-12 rounded-[2.5rem] shadow-xl border border-gold/20 relative overflow-hidden" data-id="element-105">
            <div className="absolute top-0 left-0 w-full h-2 bg-gold" data-id="element-106"></div>
            <ShieldCheck className="w-16 h-16 text-gold mx-auto mb-6" data-id="element-107" />
            <h2 className="text-2xl md:text-3xl font-bold text-forest mb-6" data-id="element-108">
              La Garantía de Claridad
            </h2>
            <p className="text-lg text-gray-700 leading-relaxed font-medium" data-id="element-109">
              En 7 días, habrás identificado al menos 3 fugas de dinero que no
              conocías y tendrás tu primer mapa financiero real en las manos.{' '}
              <br data-id="element-110" />
              <br data-id="element-111" />
              <span className="text-forest font-bold" data-id="element-112">
                La sensación de saber exactamente a dónde va cada centavo no
                tiene precio.
              </span>
            </p>
          </FadeIn>
        </div>
      </section>

      {/* 10. PRICING & FINAL CTA */}
      <section id="pricing" className="py-24 bg-forest text-white px-6 relative overflow-hidden" data-id="element-113">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[800px] bg-forest-light rounded-full blur-3xl opacity-50 -z-10 pointer-events-none" data-id="element-114"></div>

        <div className="max-w-3xl mx-auto text-center z-10 relative" data-id="element-115">
          <FadeIn data-id="element-116">
            <div className="inline-block bg-gold text-forest font-black px-6 py-2 rounded-full text-sm mb-8 uppercase tracking-widest shadow-lg" data-id="element-117">
              Oferta de Lanzamiento
            </div>
            <div className="inline-block bg-red-500 text-white font-black px-5 py-1.5 rounded-full text-sm mb-4 uppercase tracking-wide animate-pulse" data-id="element-118">
              🔥 Solo por hoy — 62% de descuento
            </div>
            <h2 className="text-4xl md:text-5xl font-black mb-6" data-id="element-119">
              Consigue el sistema completo "CFO del Hogar" por solo{' '}
              <span className="text-cream/50 line-through text-3xl md:text-4xl" data-id="element-120">
                $17.00
              </span>{' '}
              <span className="text-gold" data-id="element-121">$6.39 USD</span>
            </h2>
            <p className="text-xl text-cream/80 mb-12" data-id="element-122">
              (Un solo pago, acceso de por vida y actualizaciones incluidas).
            </p>

            <div className="flex flex-col items-center gap-6" data-id="element-123">
              <button onClick={handleAddToCart} className="w-full sm:w-auto bg-gold hover:bg-gold-light text-forest-dark font-black py-5 px-10 rounded-full shadow-xl transform transition-transform hover:scale-105 active:scale-95 flex items-center justify-center gap-3 text-xl" data-id="element-124">
                ¡QUIERO SER LA CFO DE MI HOGAR!
                <ArrowRight className="w-6 h-6" data-id="element-125" />
              </button>

              <div className="flex items-center gap-2 text-cream/70 text-sm font-medium mt-4" data-id="element-126">
                <Lock className="w-4 h-4" data-id="element-127" />
                Pago seguro con tarjeta o PayPal. Recibe tu acceso al instante.
              </div>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="bg-forest-dark text-cream/50 py-8 text-center text-sm px-6" data-id="element-128">
        <p data-id="element-129">
          © {new Date().getFullYear()} CFO del Hogar. Todos los derechos
          reservados.
        </p>
        <div className="flex items-center justify-center gap-4 mt-3" data-id="element-130">
          <button onClick={() => setModalOpen('privacy')} className="hover:text-cream transition-colors underline underline-offset-2" data-id="element-131">
            Política de Privacidad
          </button>
          <span data-id="element-132">·</span>
          <button onClick={() => setModalOpen('terms')} className="hover:text-cream transition-colors underline underline-offset-2" data-id="element-133">
            Términos y Condiciones
          </button>
        </div>
      </footer>

      {/* MODAL */}
      <AnimatePresence data-id="element-134">
        {modalOpen && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.2
      }} className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm" onClick={() => setModalOpen(null)} data-id="element-135">
            <motion.div initial={{
          opacity: 0,
          scale: 0.95,
          y: 20
        }} animate={{
          opacity: 1,
          scale: 1,
          y: 0
        }} exit={{
          opacity: 0,
          scale: 0.95,
          y: 20
        }} transition={{
          duration: 0.25,
          ease: 'easeOut'
        }} className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[80vh] overflow-y-auto relative" onClick={e => e.stopPropagation()} data-id="element-136">
              <div className="sticky top-0 bg-white border-b border-gray-100 px-6 py-4 flex items-center justify-between rounded-t-2xl" data-id="element-137">
                <h2 className="text-xl font-bold text-forest" data-id="element-138">
                  {modalOpen === 'privacy' ? 'Política de Privacidad' : 'Términos y Condiciones'}
                </h2>
                <button onClick={() => setModalOpen(null)} className="p-2 rounded-full hover:bg-gray-100 transition-colors" data-id="element-139">
                  <X className="w-5 h-5 text-gray-500" data-id="element-140" />
                </button>
              </div>

              <div className="px-6 py-6 text-gray-700 leading-relaxed space-y-4" data-id="element-141">
                {modalOpen === 'privacy' ? <>
                    <p className="text-sm text-gray-500" data-id="element-142">
                      Última actualización:{' '}
                      {new Date().toLocaleDateString('es-ES', {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric'
                })}
                    </p>
                    <h3 className="text-lg font-semibold text-forest" data-id="element-143">
                      1. Información que Recopilamos
                    </h3>
                    <p data-id="element-144">
                      Recopilamos únicamente la información necesaria para
                      procesar tu compra y entregarte el producto digital,
                      incluyendo tu nombre y dirección de correo electrónico.
                    </p>
                    <h3 className="text-lg font-semibold text-forest" data-id="element-145">
                      2. Uso de la Información
                    </h3>
                    <p data-id="element-146">
                      Tu información se utiliza exclusivamente para: procesar tu
                      pedido, enviarte el acceso al producto y comunicarte
                      actualizaciones importantes relacionadas con tu compra.
                    </p>
                    <h3 className="text-lg font-semibold text-forest" data-id="element-147">
                      3. Protección de Datos
                    </h3>
                    <p data-id="element-148">
                      Implementamos medidas de seguridad estándar de la
                      industria para proteger tu información personal. No
                      vendemos, intercambiamos ni transferimos tu información a
                      terceros.
                    </p>
                    <h3 className="text-lg font-semibold text-forest" data-id="element-149">
                      4. Cookies
                    </h3>
                    <p data-id="element-150">
                      Nuestro sitio puede utilizar cookies para mejorar tu
                      experiencia de navegación. Puedes configurar tu navegador
                      para rechazar cookies si lo prefieres.
                    </p>
                    <h3 className="text-lg font-semibold text-forest" data-id="element-151">
                      5. Tus Derechos
                    </h3>
                    <p data-id="element-152">
                      Tienes derecho a acceder, corregir o eliminar tu
                      información personal en cualquier momento.
                    </p>
                  </> : <>
                    <p className="text-sm text-gray-500" data-id="element-153">
                      Última actualización:{' '}
                      {new Date().toLocaleDateString('es-ES', {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric'
                })}
                    </p>
                    <h3 className="text-lg font-semibold text-forest" data-id="element-154">
                      1. Producto Digital
                    </h3>
                    <p data-id="element-155">
                      "CFO del Hogar" es un producto digital descargable que
                      incluye guías, plantillas y herramientas en formato PDF.
                      Al completar tu compra, recibirás acceso inmediato al
                      contenido.
                    </p>
                    <h3 className="text-lg font-semibold text-forest" data-id="element-156">
                      2. Uso Permitido
                    </h3>
                    <p data-id="element-157">
                      El producto es para uso personal únicamente. Queda
                      prohibida la reproducción, distribución o reventa del
                      contenido sin autorización expresa.
                    </p>
                    <h3 className="text-lg font-semibold text-forest" data-id="element-158">
                      3. Política de Reembolso
                    </h3>
                    <p data-id="element-159">
                      Debido a la naturaleza digital del producto, todas las
                      ventas son finales. Sin embargo, si experimentas problemas
                      técnicos para acceder al contenido, te ayudaremos a
                      resolverlos.
                    </p>
                    <h3 className="text-lg font-semibold text-forest" data-id="element-160">
                      4. Resultados
                    </h3>
                    <p data-id="element-161">
                      Los resultados financieros pueden variar según la
                      situación individual de cada persona. El contenido es
                      educativo y no constituye asesoría financiera profesional.
                    </p>
                    <h3 className="text-lg font-semibold text-forest" data-id="element-162">
                      5. Modificaciones
                    </h3>
                    <p data-id="element-163">
                      Nos reservamos el derecho de actualizar estos términos en
                      cualquier momento. Los cambios serán efectivos desde su
                      publicación en esta página.
                    </p>
                  </>}
              </div>
            </motion.div>
          </motion.div>}
      </AnimatePresence>
    </div>;
}